import { useMemo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import currency from 'currency.js'
import { Divider, Grid, Image } from 'semantic-ui-react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'

import {
  GridRowColumn,
  makeGridConfig,
} from '@/componentsV1/BaseComponents/Grid'
import {
  Text,
  Card,
  FormikInput,
  getFieldNames,
  makeNumberSchema,
  Icon,
  Tab,
  Modal,
  Button,
} from '@/base'
import FormFlowFooter from '@/componentsV1/FormFlow/FormFlowFooter'
import {
  centsToDollars,
  dollarsToCents,
  formatCurrency,
} from '@/utils/currencyHelpers'
import IncomeTab from '@/featuresV1/GoalSetting/IncomeTab'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchUserTransactions } from '@/featuresV1/Transactions/transactions.slice'
import ExpensesTab from '@/featuresV1/GoalSetting/ExpensesTab'
import ProfitTab from '@/featuresV1/GoalSetting/ProfitTab'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { GOAL_SETTING_SCREENS } from '@/featuresV1/GoalSetting'
import { getProfitGoal } from '@/featuresV1/GoalSetting/helpers'
import IncomeCalculator from '@/featuresV1/GoalSetting/IncomeCalculator'

const tqBigSpace = makeGridConfig([10, 14, 14], true)

const SettingFinancialGoals = ({
  goBack,
}: {
  goBack: (newScreen: string | null, replace?: boolean | undefined) => void
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const fp = useReselector(getFinancialProfile)
  const track = useAnalyticsTrack()

  useEffect(() => {
    dispatch(fetchUserTransactions())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      incomeGoal: fp?.annualIncomeGoalInCents
        ? centsToDollars(fp?.annualIncomeGoalInCents)
        : '',
      expenseRatio: fp?.annualExpenseRatioGoal
        ? fp?.annualExpenseRatioGoal * 100
        : '30',
    },
    onSubmit: async ({ incomeGoal, expenseRatio }) => {
      const isInitialGoal = fp?.annualIncomeGoalInCents === null

      await dispatch(
        updateFinancialProfile(fp?.id, {
          annualIncomeGoalInCents: dollarsToCents(incomeGoal),
          annualExpenseRatioGoal: Number(expenseRatio) / 100,
        })
      )

      track(
        isInitialGoal
          ? 'Saved Initial Practice Profit Goals'
          : 'Updated Practice Profit Goals'
      )

      setModalOpen(true)
      track('Viewed Practice Profit Goals Success Modal')
    },
  })

  const {
    values: { expenseRatio, incomeGoal },
    submitForm,
    setFieldValue,
  } = formik

  const fieldNames = getFieldNames(formik)

  const profitGoal = useMemo(
    () => getProfitGoal(dollarsToCents(incomeGoal), Number(expenseRatio) / 100),
    [expenseRatio, incomeGoal]
  )

  const monthlyIncome = useMemo(
    () =>
      isNaN(Number(incomeGoal)) ? 0 : currency(incomeGoal).divide(12).value,
    [incomeGoal]
  )

  const monthlyExpense = useMemo(() => {
    if (isNaN(Number(incomeGoal)) || isNaN(Number(expenseRatio))) {
      return 0
    }
    return currency(incomeGoal).multiply(expenseRatio).divide(100).divide(12)
      .value
  }, [expenseRatio, incomeGoal])

  const monthlyProfit = useMemo(
    () => (profitGoal === 0 ? 0 : currency(profitGoal).divide(12).value),
    [profitGoal]
  )

  const panes = useMemo(
    () => [
      { menuItem: 'Income', render: () => <IncomeTab /> },
      { menuItem: 'Expenses', render: () => <ExpensesTab /> },
      { menuItem: 'Profit', render: () => <ProfitTab /> },
    ],
    []
  )
  const handleTabClick = (index: number) => {
    const tabNames = panes.map((pane) => pane.menuItem)
    track(`Clicked goal setting ${tabNames[index]} tab`)
  }

  return (
    <FormikProvider value={formik}>
      <Grid>
        <GridRowColumn {...tqBigSpace}>
          <Text as="display2" textAlign="center">
            Setting Financial Goals
          </Text>
        </GridRowColumn>
        <GridRowColumn {...tqBigSpace}>
          <Text as="bodyLg">
            Data benchmarks have been provided for your context. Adjust income
            and expense targets to see how they affect profit.
          </Text>
        </GridRowColumn>
        <GridRowColumn {...tqBigSpace}>
          <Card type="subsection" backgroundColor="natural">
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FormikInput
                    label="Annual Income Goal"
                    name={fieldNames.incomeGoal}
                    schema={makeNumberSchema({
                      minNumber: 0,
                      allowedDecimals: 2,
                    })}
                    componentType="currency"
                    onBlur={() => track('Updated annual income goal input')}
                  />
                  {monthlyIncome ? (
                    <Text color="darkGray">
                      Average of{' '}
                      {formatCurrency(monthlyIncome, {
                        hideDecimalsIfZero: true,
                      })}
                      /mo
                    </Text>
                  ) : (
                    // this adds spacing equal to above so content does not jump
                    <Text>&nbsp;</Text>
                  )}
                </Grid.Column>
                <Grid.Column width={8}>
                  <FormikInput
                    label="Expense Ratio Goal"
                    name={fieldNames.expenseRatio}
                    type="number"
                    schema={makeNumberSchema({ maxNumber: 100, minNumber: 0 })}
                    rightIcon="percent"
                    onBlur={() => track('Updated expense ratio goal input')}
                  />
                  {Boolean(monthlyExpense) && (
                    <Text color="darkGray">
                      Average of{' '}
                      {formatCurrency(monthlyExpense, {
                        hideDecimalsIfZero: true,
                      })}
                      /mo
                    </Text>
                  )}
                </Grid.Column>
              </Grid.Row>
              <GridRowColumn>
                <Card type="subsection" backgroundColor="blush">
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={12}>
                        <Text as="h3">Annual Profit Goal</Text>
                        <br />
                        <Text as="display2">
                          {formatCurrency(profitGoal, {
                            hideDecimalsIfZero: true,
                          })}
                        </Text>
                        <br />
                        {monthlyProfit ? (
                          <Text as="bodySm" color="darkGray">
                            Average of{' '}
                            {formatCurrency(monthlyProfit, {
                              hideDecimalsIfZero: true,
                            })}
                            /mo
                          </Text>
                        ) : (
                          // this adds spacing equal to above so content does not jump
                          <Text as="bodySm">&nbsp;</Text>
                        )}
                      </Grid.Column>
                      <Grid.Column width={4} verticalAlign="middle">
                        <Image
                          src="https://heard-images.s3.amazonaws.com/assets/coins-stack-sparkle.svg"
                          style={{
                            width: 80,
                            height: 80,
                            backgroundColor: 'transparent',
                          }}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Card>
              </GridRowColumn>
            </Grid>
          </Card>
        </GridRowColumn>

        <GridRowColumn {...tqBigSpace}>
          <Tab panes={panes} onTabClick={handleTabClick} />
        </GridRowColumn>

        <GridRowColumn {...tqBigSpace}>
          <IncomeCalculator
            currentGoal={incomeGoal ? Number(incomeGoal) : null}
            updateIncomeGoal={(val) =>
              setFieldValue(fieldNames.incomeGoal, val)
            }
          />
        </GridRowColumn>

        <GridRowColumn {...tqBigSpace}>
          <Text as="h2">Learn More</Text>
          <Text color="darkGray">
            Need more guidance? Check out these resources for more help.
          </Text>
        </GridRowColumn>

        <Grid.Row>
          <Grid.Column computer={3} tablet={1} mobile={1} />
          <Grid.Column computer={5} tablet={7} mobile={7}>
            <Card
              type="subsection"
              backgroundColor="stone40"
              onClick={() => {
                track('Clicked "state of private practice report" resource')
                window.open(
                  'https://7507639.fs1.hubspotusercontent-na1.net/hubfs/7507639/Heard%202025%20Financial%20State%20of%20Private%20Practice%20Report.pdf',
                  '_blank'
                )
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text as="eyebrow" color="darkGray">
                  <Icon
                    icon={regular('file-contract')}
                    style={{ marginRight: 8 }}
                  />
                  Report
                </Text>
                <Icon icon={regular('square-arrow-up-right')} />
              </div>
              <Divider style={{ marginTop: 5 }} />
              <Text as="h3">State of Private Practice Report</Text>
            </Card>
          </Grid.Column>
          <Grid.Column computer={5} tablet={7} mobile={7}>
            <Card
              type="subsection"
              backgroundColor="stone40"
              onClick={() => {
                track('Clicked "tracking therapy practice expenses" resource')
                window.open(
                  'https://www.joinheard.com/articles/how-to-track-expenses-for-your-therapy-practice',
                  '_blank'
                )
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text as="eyebrow" color="darkGray">
                  <Icon
                    icon={regular('newspaper')}
                    style={{ marginRight: 8 }}
                  />
                  Article
                </Text>
                <Icon icon={regular('square-arrow-up-right')} />
              </div>
              <Divider style={{ marginTop: 5 }} />
              <Text as="h3">
                How to Track Expenses for Your Therapy Practice
              </Text>
            </Card>
          </Grid.Column>
        </Grid.Row>

        <FormFlowFooter
          onBack={() => goBack(GOAL_SETTING_SCREENS.intro)}
          onForward={submitForm}
          continueButtonText="Save Goal"
        />
      </Grid>
      <Modal open={modalOpen} onClose={() => navigate('/')} closeIcon>
        <Modal.Header>Goals Saved</Modal.Header>
        <Modal.Content>
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/check.svg"
            style={{ height: 61, width: 61, backgroundColor: 'transparent' }}
          />
          <br />
          <Text>
            We’ve saved your goals. You’ll now be able to track the progress of
            your income, expense, and profit targets. We recommend you
            periodically refresh your goals as you progress throughout the year.
          </Text>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => navigate('/')}>Done</Button>
        </Modal.Actions>
      </Modal>
    </FormikProvider>
  )
}

export default SettingFinancialGoals
